/**
 * The firm's contacts — every person, once, for every project (0018/0030).
 *
 * The same address book the project pages read; this page is where it is
 * curated. Everyone in the firm can add, edit and remove, because the rules
 * have been firm-scoped since 0030 — there is no per-person ownership to
 * respect here.
 *
 * Searching, the usage counts and the delete warning are all in
 * `@/lib/contacts-library`, which is tested. This renders them.
 */

import { esc, setPageTitle } from '@/lib/dom';
import { getShell } from '@/lib/shell-handle';
import { openContactModal } from '@/lib/contact-modal';
import { listDesignerContacts, contactUsage } from '@/lib/data/contacts';
import { forgetAllProjects } from '@/lib/data/project-workspace';
import { contactTypeLabel } from '@/lib/contact-display';
import {
  searchContacts, usageLabel, CONTACT_TYPE_FILTERS, NO_USAGE,
  type ContactTypeFilter, type ContactUsage,
} from '@/lib/contacts-library';
import type { DesignerContactRow } from '@/types/database';

export async function renderContactsLibrary(root: HTMLElement): Promise<void> {
  root.innerHTML = `<section class="page"><div class="loading">Loading contacts…</div></section>`;
  setPageTitle('Contacts');
  getShell()?.setContext({ kind: 'global', active: 'contacts' });
  try {
    const [contacts, usage] = await Promise.all([
      listDesignerContacts(),
      // A count is a nicety; the list is the page. Losing the counts must not
      // lose the contacts.
      contactUsage().catch(() => new Map<string, ContactUsage>()),
    ]);
    paint(root, contacts, usage);
  } catch (err) {
    root.innerHTML = `<section class="page"><div class="auth-error">${
      esc(err instanceof Error ? err.message : 'Could not load your contacts')}</div></section>`;
  }
}

function paint(root: HTMLElement, contacts: DesignerContactRow[], usage: Map<string, ContactUsage>): void {
  const reload = (): void => {
    // A contact edited here appears on every project they're on, and deleting
    // one reaches all of them — so no cached project survives this.
    forgetAllProjects();
    void renderContactsLibrary(root);
  };
  let query = '';
  let type: ContactTypeFilter = 'all';

  root.innerHTML = `
    <section class="page">
      <div class="page-head"><h1>Contacts</h1></div>
      <p class="muted library-intro">
        Everyone your firm works with, in one place. A contact added here can be put on any project —
        editing them here updates them everywhere they appear.
      </p>
      <section class="panel">
        <div class="section-bar library-toolbar">
          <div class="section-bar-left">
            <input type="search" class="library-search" data-contact-search
                   placeholder="Search by name, company, email or phone" />
            <select data-contact-type>
              ${CONTACT_TYPE_FILTERS.map((f) => `<option value="${esc(f.value)}">${esc(f.label)}</option>`).join('')}
            </select>
          </div>
          <button class="btn-primary" data-action="add-contact">+ Add contact</button>
        </div>
        ${contacts.length === 0 ? `
          <div class="empty-inline">
            No contacts yet. Add clients, contractors and vendors here, or from any project’s Contacts page.
          </div>` : `
          <div class="contact-rows" data-contact-rows></div>
          <div class="muted small library-count" data-contact-count></div>`}
      </section>
    </section>`;

  const rowsEl = root.querySelector<HTMLElement>('[data-contact-rows]');
  const countEl = root.querySelector<HTMLElement>('[data-contact-count]');

  const draw = (): void => {
    if (!rowsEl || !countEl) return;
    const shown = searchContacts(contacts, query, type);
    rowsEl.innerHTML = shown.length === 0
      ? `<div class="empty-inline">No contacts match that search.</div>`
      : shown.map((c) => renderRow(c, usage.get(c.id) ?? NO_USAGE)).join('');
    countEl.textContent = shown.length === contacts.length
      ? `${contacts.length} contact${contacts.length === 1 ? '' : 's'}`
      : `${shown.length} of ${contacts.length} contacts`;

    // querySelectorAll — one button per row, and the row is the button.
    rowsEl.querySelectorAll<HTMLButtonElement>('[data-edit-contact]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const c = contacts.find((x) => x.id === btn.dataset.editContact);
        if (c) openContactModal({ contact: c, usage: usage.get(c.id) ?? NO_USAGE, onDone: reload });
      });
    });
  };

  root.querySelectorAll<HTMLInputElement>('[data-contact-search]').forEach((el) => {
    el.addEventListener('input', () => { query = el.value; draw(); });
  });
  root.querySelectorAll<HTMLSelectElement>('[data-contact-type]').forEach((el) => {
    el.addEventListener('change', () => { type = el.value as ContactTypeFilter; draw(); });
  });
  root.querySelectorAll('[data-action="add-contact"]').forEach((el) => {
    el.addEventListener('click', () => {
      // No projectId: this creates a contact in the library and puts them on
      // no project, which is what this page is for.
      openContactModal({ onDone: reload });
    });
  });

  draw();
}

function renderRow(c: DesignerContactRow, u: ContactUsage): string {
  const types = contactTypeLabel(c);
  return `
    <button class="contact-row" data-edit-contact="${esc(c.id)}">
      <span class="contact-row-main">
        <span class="contact-row-name">${esc(c.name)}</span>
        ${c.company ? `<span class="muted"> — ${esc(c.company)}</span>` : ''}
        ${types ? `<span class="contact-row-types">${esc(types)}</span>` : ''}
      </span>
      <span class="contact-row-detail muted small">
        ${[c.email, c.phone].filter(Boolean).map((x) => esc(String(x))).join(' · ') || '—'}
      </span>
      <span class="contact-row-usage muted small">${esc(usageLabel(u))}</span>
    </button>`;
}
